import { Maximize2, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui'
import { useChatMutations } from '@/modules/chat/hooks'
import { useAsyncError } from '@/modules/errors'
import { cn } from '@/utils'
import {
  ConfirmationFooter,
  useConfirmationCardState,
} from './confirmationCard'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'

// Sample rows are camelCased from lukita's TransactionPresenter.row by
// artifactKinds.ts (deep), so this artifact uses one convention.
export interface RemoveTransactionsPreviewSampleRow {
  id: number
  date?: string | null
  description?: string | null
  merchant?: string | null
  amount?: number | null
  accountNumber?: string | null
  statementFileName?: string | null
}

export interface RemoveTransactionsPreviewSpec {
  batchToken: string
  affectedCount: number
  sample: RemoveTransactionsPreviewSampleRow[]
  warning?: string
  expiresAt: string
}

interface RemoveTransactionsPreviewArtifactProps {
  slug: string
  spec: RemoveTransactionsPreviewSpec
}

export default function RemoveTransactionsPreviewArtifact({
  slug,
  spec,
}: RemoveTransactionsPreviewArtifactProps) {
  const { sendMessage } = useChatMutations()
  const throwAsyncError = useAsyncError()
  const { state, disabled, setRequested, setCancelledLocally } =
    useConfirmationCardState({
      expiresAt: spec.expiresAt,
      applyToolName: 'apply_remove_transactions',
      tokenField: 'batch_token',
      token: spec.batchToken,
    })
  const [expanded, setExpanded] = useState(false)

  function applyRemoval() {
    setRequested(true)
    sendMessage(slug, 'Remove the transactions.', {
      kind: 'remove_transactions',
      batchToken: spec.batchToken,
    }).catch((error: Error) => {
      setRequested(false)
      throwAsyncError(error)
    })
  }

  function cancelRemoval() {
    setCancelledLocally(true)
  }

  const headline = `Remove ${spec.affectedCount.toLocaleString()} ${pluralize('transaction', spec.affectedCount)}`

  const footerProps = {
    expiresAt: spec.expiresAt,
    state,
    disabled,
    appliedMessage: 'Removed. Ask to undo to restore.',
    expiredMessage: 'Expired. Ask the assistant to preview again.',
    confirmLabel: (
      <span className="inline-flex items-center gap-1.5">
        <Trash2 className="size-3.5" aria-hidden />
        Remove {spec.affectedCount.toLocaleString()}
      </span>
    ),
  }

  return (
    <figure className="my-1 w-full overflow-hidden rounded-xl border bg-card shadow-sm">
      <figcaption className="flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm font-medium text-foreground">
        <span className="flex min-w-0 items-center gap-2">
          <Trash2 className="size-4 shrink-0 text-destructive" aria-hidden />
          <span className="truncate">{headline}</span>
        </span>
        {spec.sample.length > 0 && (
          <Button
            type="button"
            theme="ghost"
            size="xs"
            isIcon
            className="shrink-0"
            onClick={() => setExpanded(true)}
            title="Expand"
            aria-label="Expand"
          >
            <Maximize2 className="size-3.5" aria-hidden />
          </Button>
        )}
      </figcaption>

      <CautionBar warning={spec.warning} />

      {spec.sample.length > 0 && <SampleTable sample={spec.sample} />}

      <ConfirmationFooter
        {...footerProps}
        onApply={applyRemoval}
        onCancel={cancelRemoval}
      />

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={headline}
      >
        <CautionBar warning={spec.warning} />
        <SampleTable
          sample={spec.sample}
          expanded
          className="max-h-none flex-1"
        />
        <ConfirmationFooter
          {...footerProps}
          onApply={() => {
            setExpanded(false)
            applyRemoval()
          }}
          onCancel={() => {
            setExpanded(false)
            cancelRemoval()
          }}
        />
      </ExpandedArtifactDialog>
    </figure>
  )
}

function CautionBar({ warning }: { warning?: string }) {
  return (
    <div className="flex gap-2 border-b bg-amber-50 px-4 py-2 text-[12px] text-amber-900">
      <span aria-hidden>⚠</span>
      <span>
        {warning ??
          'These transactions will be hidden from totals, insights, and exports. This is reversible.'}
      </span>
    </div>
  )
}

interface SampleTableProps {
  sample: RemoveTransactionsPreviewSampleRow[]
  expanded?: boolean
  className?: string
}

function SampleTable({
  sample,
  expanded = false,
  className,
}: SampleTableProps) {
  const descriptionMaxWidth = expanded ? 'max-w-[640px]' : 'max-w-[280px]'
  return (
    <div
      className={cn(
        'max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent',
        className,
      )}
    >
      <table className="w-full text-[13px]">
        <thead className="sticky top-0 z-10 border-b bg-card text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Description</th>
            <th className="px-3 py-2 text-left">Account</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {sample.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                {row.date ?? ''}
              </td>
              <td className="px-3 py-2">
                <div
                  className={cn('truncate', descriptionMaxWidth)}
                  title={row.description ?? ''}
                >
                  {row.description ?? row.merchant ?? ''}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                {row.accountNumber ?? ''}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                {formatAmount(row.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function formatAmount(amount?: number | null): string {
  if (amount == null) return ''
  return CURRENCY.format(amount)
}

function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`
}
